/**
 * Photo-plate mockups: per-vertical flat-lay photos with blank pack pieces,
 * and the measured geometry that live components are projected onto.
 *
 * The plates were generated (gpt-5.4-image-2 via OpenRouter, layout locked by
 * a reference image) with every blank at the pack's real proportions — cards
 * 2:1, hangers 1:2.6 with the die-cut hole, sign 4:3, magnet ~2:1 (the photos
 * predate the magnet moving to the sourced 18×12in / 3:2 product; `reflow`
 * re-lays the piece out onto whatever box the blank measured, so the change
 * costs type scale, never distortion). Geometry
 * below was MEASURED, not eyeballed: connected-component analysis over the
 * white blanks in the browser (threshold → components → PCA for centre, size
 * and rotation; hanger anchors from their die-cut holes plus a top-edge line
 * fit for the angle). Regenerating a plate means re-measuring it — see
 * fnd-uh5's notes for the scratchpad analyzer.
 *
 * Coordinates are in the plate image's own 1024×1024 pixel space; `a` is
 * degrees clockwise. `w`/`h` are the blank's long/short sides — the renderer
 * reflows the live piece onto that box unless the rect pins another `fit`.
 */

export const PLATE_IMG = 1024;

export interface PlateRect {
  cx: number;
  cy: number;
  w: number;
  h: number;
  a: number;
  /**
   * How the live piece meets the blank. Omitted means `reflow`, which is what
   * a blank almost always wants:
   *   'reflow'  — (default) the piece is RE-LAID-OUT on a canvas with the
   *               box's aspect: type keeps its designed size and centred
   *               content stays centred, nothing is distorted, and the blank
   *               is still filled edge to edge;
   *   'contain' — keeps the piece's own aspect, centred, accepting a margin
   *               where the blank's aspect differs. Worth pinning only when a
   *               piece must not re-wrap;
   *   'stretch' — scales each axis to fill the box exactly, which DOES distort
   *               the artwork. Only for a per-cent or two of correction where
   *               a margin would read worse.
   */
  fit?: 'contain' | 'reflow' | 'stretch';
  /** Corner rounding, plate px — clips the content like Figma's clip mode. */
  radius?: number;
  /**
   * Per-corner offsets (tl, tr, br, bl) from the box's square corners, in the
   * piece's rotated frame — rendered as a matrix3d homography for blanks the
   * camera saw in slight perspective.
   */
  quad?: [number, number][];
  /**
   * Door hangers only: where the plate punches the die-cut, in plate px —
   * `cy` down from the blank's top edge, `cx` off its centre line, `r` the
   * horizontal radius and `ry` the vertical one (defaults to `r`, so a plain
   * circle needs neither). A camera looking down at an angle turns a punched
   * circle into an ellipse, which is why `ry` exists. The photo's own hole was
   * drawn by the image model and never lands exactly on the hanger's printed
   * geometry, so it is measured per blank; omitted falls back to the hanger's
   * own printed hole scaled onto the blank.
   */
  hole?: { cy: number; r: number; cx?: number; ry?: number };
}

export interface PlateConfig {
  pieces: {
    /** Absent on plates whose photo has no sign blank (the duo plate). */
    sign?: PlateRect;
    magnet: PlateRect;
    /** Top face of the card stack — wears the card front. */
    stack: PlateRect;
    /** Loose card, upper — front. */
    cardA: PlateRect;
    /** Loose card, lower — the dark review back. */
    cardB: PlateRect;
    /** Absent on plates whose photo has no hanger blanks (the duo plate). */
    hangers?: PlateRect[];
  };
}

/**
 * Every plate below is hand-aligned in the lab (/lab/pack -> Tune plate) and
 * saved from there; the numbers are pasted back in wholesale rather than
 * tweaked by hand here. Die-cut holes were first measured off the photos —
 * paper level from a band below the hole, then the longest contiguous run
 * deviating from it down the blank centre line, because plain thresholding
 * (fixed or Otsu) fails on neutral, whose hole interior sits only ~20 levels
 * under the paper. Those measurements are now folded into the tuned values.
 */
export const PLATES: Record<string, PlateConfig> = {
  /**
   * The physical pack, exactly as it ships: card stack, two loose cards, one
   * car magnet, a kraft mailer intruding from the corner. No sign, no hangers
   * — the partial-plate case. Measured 2026-08-31 (CCA thresholds 245/238;
   * stack top face nudged off its own bright side faces), pre-tune.
   */
  duo: {
    pieces: {
      magnet: { cx: 357, cy: 320, w: 453, h: 260, a: -1, radius: 8, quad: [[8, -4], [3, -4], [0, 1], [0, 0]] },
      stack: { cx: 789, cy: 482, w: 272, h: 136, a: -0.3, radius: 2 },
      cardA: { cx: 230, cy: 637, w: 248, h: 126, a: -6.4, radius: 2 },
      cardB: { cx: 500, cy: 717, w: 249, h: 134, a: 8.2, radius: 2 },
    },
  },
  neutral: {
    pieces: {
      sign: { cx: 323, cy: 693, w: 493, h: 343, a: -0.6, quad: [[10, -7], [4, 4], [1, 5], [-12, -4]] },
      magnet: { cx: 780, cy: 754, w: 327, h: 196, a: -0.1, quad: [[-3, -3], [4, -3], [4, 3], [-3, -1]] },
      stack: { cx: 254, cy: 184, w: 172, h: 92, a: -5.4, quad: [[-5, 0], [0, 0], [1, 1], [-4, 2]] },
      cardA: { cx: 219, cy: 321, w: 155, h: 74, a: -9.1, quad: [[1, 0], [2, -2], [6, 0], [3, 2]] },
      cardB: { cx: 298, cy: 403, w: 144, h: 74, a: -13.1, quad: [[1, -1], [2, 2], [2, 4], [1, 2]] },
      hangers: [
        { cx: 564, cy: 319, w: 127, h: 337, a: -9.9, hole: { cx: -0.5, cy: 45, r: 25 }, quad: [[1, -3], [-2, -3], [8, -5], [10, -2]] },
        { cx: 693, cy: 307, w: 127, h: 337, a: 2.3, hole: { cx: -1.2, cy: 46.2, r: 25 }, quad: [[5, -4], [-2, -4], [11, -2], [18, 1]] },
        { cx: 833, cy: 323, w: 127, h: 327, a: 10.2, hole: { cx: -1.1, cy: 45.3, r: 25 }, quad: [[5, -5], [1, -4], [2, 5], [3, 1]] },
      ],
    },
  },
  plumbing: {
    pieces: {
      sign: { cx: 352, cy: 692, w: 534, h: 355, a: -0.8, quad: [[12, -3], [0, 4], [1, 3], [-10, -3]] },
      magnet: { cx: 818, cy: 775, w: 303, h: 152, a: 0.7, radius: 24, quad: [[-5, 1], [0, -3], [7, 1], [1, 5]] },
      stack: { cx: 313, cy: 198, w: 175, h: 95, a: -5, radius: 2, quad: [[0, 0], [-3, 0], [-1, -1], [0, -1]] },
      cardA: { cx: 161, cy: 348, w: 166, h: 79, a: -11.1, quad: [[0, 0], [-3, 1], [-1, 0], [0, 0]] },
      cardB: { cx: 300, cy: 413, w: 161, h: 79, a: -13.6, radius: 2, quad: [[2, -1], [0, -1], [4, 1], [1, 3]] },
      hangers: [
        { cx: 579, cy: 322, w: 130, h: 326, a: -6.8, radius: 2, hole: { cx: -1.5, cy: 41.7, r: 27.1, ry: 24.9 }, quad: [[-3, -3], [0, -1], [6, 2], [-2, 3]] },
        { cx: 714, cy: 323, w: 128, h: 316, a: 8.7, radius: 2, hole: { cx: -0.9, cy: 41.9, r: 27.1, ry: 25 }, quad: [[-1, -1], [1, -5], [19, 5], [16, 10]] },
        { cx: 867, cy: 363, w: 130, h: 326, a: 13.3, radius: 2, hole: { cx: -3.7, cy: 44.8, r: 27.1 }, quad: [[-3, -3], [0, 0], [3, 1], [-11, -4]] },
      ],
    },
  },
  hvac: {
    pieces: {
      sign: { cx: 355, cy: 697, w: 460, h: 335, a: -0.6, quad: [[5, -5], [2, 0], [2, 4], [-9, -1]] },
      magnet: { cx: 803, cy: 736, w: 311, h: 235, a: 1.1, radius: 24, quad: [[-7, -4], [-6, -4], [2, -1], [-3, 1]] },
      stack: { cx: 252, cy: 217, w: 164, h: 90, a: -1.1, radius: 2 },
      cardA: { cx: 180, cy: 349, w: 153, h: 78, a: -12.4, quad: [[1, -3], [5, 0], [4, 3], [-1, 3]] },
      cardB: { cx: 291, cy: 418, w: 156, h: 80, a: -15.9 },
      hangers: [
        { cx: 520, cy: 334, w: 110, h: 306, a: -12.4, radius: 2, hole: { cx: -1.6, cy: 51.3, r: 27.3 }, quad: [[-1, -5], [3, -3], [3, -1], [-1, -1]] },
        { cx: 654, cy: 325, w: 120, h: 306, a: 1.5, radius: 2, hole: { cx: -0.6, cy: 50.5, r: 27.3 }, quad: [[1, -3], [-1, -4], [2, 1], [0, -2]] },
        { cx: 796, cy: 342, w: 110, h: 306, a: 11.5, radius: 2, hole: { cx: 0.3, cy: 50.5, r: 27.3 }, quad: [[-4, -6], [3, -5], [5, 0], [-5, -6]] },
      ],
    },
  },
  electrical: {
    pieces: {
      sign: { cx: 330, cy: 728, w: 464, h: 335, a: -0.6, radius: 2, quad: [[0, -10], [7, -4], [6, -17], [-17, -24]] },
      magnet: { cx: 799, cy: 774, w: 311, h: 155, a: 1.1, radius: 24, quad: [[-11, -6], [-2, -9], [9, 0], [-5, 5]] },
      stack: { cx: 284, cy: 207, w: 174, h: 90, a: -1.6, radius: 2, quad: [[-6, -5], [4, -6], [5, 7], [-4, 7]] },
      cardA: { cx: 214, cy: 360, w: 163, h: 78, a: -12.4, radius: 2, quad: [[-1, -6], [6, -5], [5, -3], [0, -3]] },
      cardB: { cx: 293, cy: 436, w: 156, h: 80, a: -12.4, radius: 2, quad: [[-1, -1], [3, 1], [3, 2], [-3, 2]] },
      hangers: [
        { cx: 558, cy: 354, w: 110, h: 326, a: -10.9, radius: 2, hole: { cx: 0.1, cy: 42.1, r: 24.9, ry: 23.3 }, quad: [[-8, -9], [4, -8], [5, -4], [-7, -4]] },
        { cx: 681, cy: 350, w: 120, h: 326, a: 6.5, radius: 2, hole: { cx: 0.6, cy: 43, r: 27.3, ry: 24 }, quad: [[-2, -8], [1, -8], [4, -4], [5, -4]] },
        { cx: 820, cy: 379, w: 110, h: 316, a: 14, radius: 2, hole: { cx: -0.1, cy: 42.7, r: 25.3, ry: 25.1 }, quad: [[-4, -14], [4, -12], [5, 2], [-8, -4]] },
      ],
    },
  },
  roofing: {
    pieces: {
      sign: { cx: 356, cy: 691, w: 460, h: 335, a: -0.6, quad: [[1, -3], [-6, 1], [-7, -31], [-12, -39]] },
      magnet: { cx: 803, cy: 728, w: 311, h: 235, a: 1.1, radius: 24, quad: [[-11, -6], [15, -9], [23, -40], [-10, -40]] },
      stack: { cx: 212, cy: 185, w: 164, h: 80, a: -5.1, radius: 2, quad: [[-6, -5], [6, -4], [6, 3], [-6, 2]] },
      cardA: { cx: 162, cy: 323, w: 153, h: 78, a: -10.4, quad: [[-3, -4], [-3, -2], [-4, -3], [-4, -3]] },
      cardB: { cx: 257, cy: 401, w: 156, h: 80, a: -11.4, quad: [[0, -1], [-4, 0], [-4, -1], [0, 0]] },
      hangers: [
        { cx: 512, cy: 293, w: 110, h: 306, a: -5.9, radius: 2, hole: { cx: -0.5, cy: 46.7, r: 25.4, ry: 27.3 }, quad: [[-6, -3], [-2, -2], [2, -2], [-5, -3]] },
        { cx: 673, cy: 301, w: 120, h: 306, a: 2.5, radius: 2, hole: { cx: -1.5, cy: 47.2, r: 26.6, ry: 27.3 }, quad: [[4, -5], [-1, -4], [2, 1], [0, -1]] },
        { cx: 845, cy: 325, w: 110, h: 306, a: 8.5, radius: 2, hole: { cx: -0.4, cy: 46.5, r: 26.8, ry: 27.3 }, quad: [[-4, -10], [-2, -8], [2, -1], [-6, -4]] },
      ],
    },
  },
  cleaning: {
    pieces: {
      sign: { cx: 419, cy: 719, w: 480, h: 345, a: -0.6, quad: [[-4, -9], [3, -5], [4, 10], [-13, 5]] },
      magnet: { cx: 837, cy: 745, w: 269, h: 187, a: 1.6, radius: 20, quad: [[-12, -7], [-3, -8], [4, -1], [-6, 1]] },
      stack: { cx: 316, cy: 204, w: 164, h: 90, a: -3.1, radius: 2, quad: [[-5, -7], [5, -3], [5, 9], [-7, 6]] },
      cardA: { cx: 257, cy: 343, w: 153, h: 78, a: -12.4, quad: [[2, -1], [5, 0], [5, 5], [2, 5]] },
      cardB: { cx: 355, cy: 432, w: 156, h: 80, a: -13.4, quad: [[-2, -10], [-4, -5], [-7, 0], [-3, -3]] },
      hangers: [
        { cx: 572, cy: 335, w: 110, h: 326, a: -7.4, radius: 2, hole: { cx: -0.5, cy: 38.8, r: 24.9, ry: 23.4 }, quad: [[-2, -8], [2, -6], [2, -6], [-3, -7]] },
        { cx: 708, cy: 330, w: 100, h: 316, a: 5, radius: 2, hole: { cx: 0.9, cy: 40.3, r: 25, ry: 24.9 }, quad: [[-5, -9], [1, -8], [9, 3], [2, 4]] },
        { cx: 854, cy: 351, w: 110, h: 320, a: 7.5, radius: 2, hole: { cx: -1.1, cy: 41.9, r: 27.3 }, quad: [[0, -13], [-1, -8], [-7, 1], [-9, -4]] },
      ],
    },
  },
  refinishing: {
    pieces: {
      sign: { cx: 331, cy: 676, w: 460, h: 335, a: -0.6, quad: [[-5, -9], [2, 0], [2, 4], [-20, -1]] },
      magnet: { cx: 803, cy: 694, w: 337, h: 225, a: 1.1, radius: 14, quad: [[-11, -4], [-4, -4], [6, -9], [-6, -8]] },
      stack: { cx: 268, cy: 204, w: 164, h: 90, a: -5.6, radius: 2 },
      cardA: { cx: 225, cy: 347, w: 147, h: 78, a: -13.4, radius: 2, quad: [[-9, -8], [13, -3], [12, 6], [-11, 1]] },
      cardB: { cx: 337, cy: 406, w: 156, h: 80, a: -15.9, radius: 2, quad: [[-3, -5], [5, -6], [6, -1], [-1, 1]] },
      hangers: [
        { cx: 584, cy: 309, w: 116, h: 296, a: -5.9, radius: 2, hole: { cx: -0.2, cy: 42, r: 23.5, ry: 26 }, quad: [[-5, -6], [7, -6], [10, -3], [-4, -3]] },
        { cx: 735, cy: 297, w: 120, h: 296, a: 3, radius: 2, hole: { cx: 0.2, cy: 43.9, r: 26.9, ry: 25.3 }, quad: [[-2, -2], [3, 1], [3, 3], [-1, 2]] },
        { cx: 889, cy: 321, w: 110, h: 296, a: 8.5, radius: 2, hole: { cx: 1, cy: 43.8, r: 21.5, ry: 23.9 }, quad: [[-10, -8], [5, -3], [8, -1], [-10, -6]] },
      ],
    },
  },
  landscaping: {
    pieces: {
      sign: { cx: 409, cy: 721, w: 466, h: 344, a: 1.4, quad: [[-4, 4], [-5, -7], [9, -4], [-4, 7]] },
      magnet: { cx: 822, cy: 759, w: 289, h: 202, a: 1.8, radius: 16, quad: [[-4, -5], [-3, -12], [7, -18], [1, -11]] },
      stack: { cx: 322, cy: 224, w: 168, h: 96, a: -2.7, radius: 2 },
      cardA: { cx: 267, cy: 354, w: 144, h: 77, a: -9.1, fit: 'stretch', radius: 2, quad: [[0, 0], [0, 0], [3, 0], [3, 3]] },
      cardB: { cx: 347, cy: 434, w: 142, h: 78, a: -11.7, radius: 2, quad: [[-4, -1], [3, -2], [6, 6], [-4, 4]] },
      hangers: [
        { cx: 555, cy: 324, w: 105, h: 260, a: -6.9, radius: 2, hole: { cx: -1.6, cy: 33, r: 22.3, ry: 20.3 }, quad: [[-4, -12], [1, -12], [7, 40], [1, 43]] },
        { cx: 690, cy: 315, w: 97, h: 270, a: 6.3, radius: 2, hole: { cx: -0.3, cy: 35.6, r: 23, ry: 21.3 }, quad: [[-3, -1], [1, -2], [14, 42], [8, 43]] },
        { cx: 817, cy: 341, w: 105, h: 300, a: 10.2, fit: 'stretch', radius: 2, hole: { cy: 44, r: 25.3 }, quad: [[0, -3], [0, -2], [6, 42], [6, 42]] },
      ],
    },
  },
};

/** Every measured plate, in wall/selector display order. */
export const PLATE_IDS = Object.keys(PLATES);

/** A rect's effective fit — `reflow` unless the plate pins something else. */
export const fitOf = (r: PlateRect): NonNullable<PlateRect['fit']> => r.fit ?? 'reflow';

/** Trade → plate. Painting shares the drop cloth; yard trades share the oak. */
export function plateForTrade(trade: string): string {
  const t = trade.trim().toLowerCase();
  if (t in PLATES) return t;
  if (t === 'painting') return 'refinishing';
  if (t === 'lawn care' || t === 'tree service' || t === 'fencing') return 'landscaping';
  return 'neutral';
}
